/**
 * Google Calendar Service - Handles OAuth and calendar sync via Edge Functions
 *
 * Production OAuth: Users click "Connect with Google" - no API keys needed
 * Token exchange happens securely on the server via Supabase Edge Functions
 */

import { getAccessToken } from '../lib/supabaseRest';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const SCOPES = [
  'https://www.googleapis.com/auth/calendar.readonly',
  'https://www.googleapis.com/auth/calendar.events',
];

export interface GoogleCalendarSettings {
  connected: boolean;
  isExpired: boolean;
  defaultCalendarId: string;
  syncEnabled: boolean;
  syncDirection: 'read' | 'write' | 'both';
  syncFrequencyMinutes: number;
  lastSyncAt: string | null;
  calendarStartHour: number;
  // Token health fields
  refreshTokenDaysRemaining?: number | null;
  oauthAppStatus?: 'testing' | 'production';
  needsReconnect?: boolean;
}

/** Status for a single connected Calendar account (multi-account) */
export interface GoogleCalendarAccountStatus extends GoogleCalendarSettings {
  accountId: string;
  accountEmail: string;
}

export interface GoogleCalendarEvent {
  id: string;
  summary: string;
  description?: string;
  start: {
    dateTime?: string;
    date?: string;
    timeZone?: string;
  };
  end: {
    dateTime?: string;
    date?: string;
    timeZone?: string;
  };
  location?: string;
  status: 'confirmed' | 'tentative' | 'cancelled';
  htmlLink?: string;
  colorId?: string;
  attendees?: Array<{
    email: string;
    displayName?: string;
    responseStatus?: 'needsAction' | 'declined' | 'tentative' | 'accepted';
  }>;
}

export interface CalendarListEntry {
  id: string;
  summary: string;
  primary?: boolean;
  backgroundColor?: string;
  accessRole: 'freeBusyReader' | 'reader' | 'writer' | 'owner';
}

export interface SyncResult {
  success: boolean;
  eventsAdded: number;
  eventsUpdated: number;
  error?: string;
}

// Local settings for UI state (sync direction for write checks)
const LOCAL_SETTINGS_KEY = 'pkm-google-calendar-local-settings';

interface LocalSettings {
  syncDirection: 'read' | 'write' | 'both';
  calendarStartHour: number;
}

// In-memory cache for getEvents responses. Keyed by calendar+range so a
// week the user has already navigated through replays instantly. TTL is
// short because external calendars may change underneath us; mutating
// methods (create/update/delete) clear the cache wholesale.
const EVENTS_CACHE_TTL_MS = 60_000;
interface EventsCacheEntry {
  events: GoogleCalendarEvent[];
  fetchedAt: number;
}

class GoogleCalendarService {
  private syncInterval: ReturnType<typeof setInterval> | null = null;
  private eventListeners: Map<string, Set<(events: GoogleCalendarEvent[]) => void>> = new Map();
  private localSettings: LocalSettings;
  private connectionStatus: GoogleCalendarSettings | null = null;
  private eventsCache: Map<string, EventsCacheEntry> = new Map();

  constructor() {
    this.localSettings = this.loadLocalSettings();
  }

  private eventsCacheKey(startDate: Date, endDate: Date, calendarId?: string): string {
    return `${calendarId ?? 'default'}|${startDate.toISOString()}|${endDate.toISOString()}`;
  }

  private invalidateEventsCache(): void {
    this.eventsCache.clear();
  }

  private loadLocalSettings(): LocalSettings {
    try {
      const stored = localStorage.getItem(LOCAL_SETTINGS_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading local Calendar settings:', error);
    }
    return { syncDirection: 'read', calendarStartHour: 6 };
  }

  private saveLocalSettings(settings: Partial<LocalSettings>): void {
    this.localSettings = { ...this.localSettings, ...settings };
    try {
      localStorage.setItem(LOCAL_SETTINGS_KEY, JSON.stringify(this.localSettings));
    } catch (error) {
      console.error('Error saving local Calendar settings:', error);
    }
  }

  /**
   * Get auth header for Edge Function calls
   * CRITICAL FIX: Now async to await token refresh if needed
   */
  private async getAuthHeader(): Promise<string> {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      throw new Error('Not logged in');
    }
    return `Bearer ${accessToken}`;
  }

  /**
   * Make authenticated request to Edge Function
   * CRITICAL FIX: Now awaits async getAuthHeader()
   */
  private async edgeFetch(
    functionName: string,
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const url = `${SUPABASE_URL}/functions/v1/${functionName}${endpoint}`;
    const authHeader = await this.getAuthHeader();
    return fetch(url, {
      ...options,
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  }

  /**
   * Initiate OAuth flow - redirects to Google
   */
  initiateOAuth(): void {
    if (!GOOGLE_CLIENT_ID) {
      throw new Error('Google Client ID not configured');
    }

    const redirectUri = `${window.location.origin}/oauth/google-callback`;
    const state = this.generateState();

    // Store state for verification
    sessionStorage.setItem('google_calendar_oauth_state', state);

    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: SCOPES.join(' '),
      access_type: 'offline',
      prompt: 'consent',
      state: state,
    });

    window.location.href = `${GOOGLE_AUTH_URL}?${params.toString()}`;
  }

  /**
   * Handle OAuth callback - exchange code for tokens via Edge Function
   */
  async handleOAuthCallback(code: string, state: string): Promise<boolean> {
    // Verify state
    const storedState = sessionStorage.getItem('google_calendar_oauth_state');
    if (state !== storedState) {
      throw new Error('OAuth state mismatch - possible CSRF attack');
    }
    sessionStorage.removeItem('google_calendar_oauth_state');

    const redirectUri = `${window.location.origin}/oauth/google-callback`;

    // Exchange code via Edge Function (keeps client_secret secure)
    const response = await this.edgeFetch('calendar-oauth', '/exchange', {
      method: 'POST',
      body: JSON.stringify({ code, redirectUri }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('[GoogleCalendar] OAuth exchange failed:', JSON.stringify(error));
      const detail = error.details ? `: ${error.details}` : '';
      throw new Error((error.error || 'Failed to connect Google Calendar') + detail);
    }

    // Clear cached status to force refresh
    this.connectionStatus = null;
    return true;
  }

  /**
   * Check connection status from Edge Function
   */
  async getConnectionStatus(): Promise<GoogleCalendarSettings> {
    try {
      const response = await this.edgeFetch('calendar-oauth', '/status');

      if (!response.ok) {
        return {
          connected: false,
          isExpired: false,
          defaultCalendarId: 'primary',
          syncEnabled: false,
          syncDirection: 'read',
          syncFrequencyMinutes: 15,
          lastSyncAt: null,
          calendarStartHour: 6,
        };
      }

      const data = await response.json();
      this.connectionStatus = data;

      // Sync local settings with server
      if (data.syncDirection) {
        this.localSettings.syncDirection = data.syncDirection;
      }
      if (data.calendarStartHour !== undefined) {
        this.localSettings.calendarStartHour = data.calendarStartHour;
      }

      return data;
    } catch (error) {
      console.error('Error checking Calendar connection:', error);
      return {
        connected: false,
        isExpired: false,
        defaultCalendarId: 'primary',
        syncEnabled: false,
        syncDirection: 'read',
        syncFrequencyMinutes: 15,
        lastSyncAt: null,
        calendarStartHour: 6,
      };
    }
  }

  /**
   * Check if connected (synchronous - uses cached status only)
   * Call getConnectionStatus() first to ensure cache is populated
   */
  isConnected(): boolean {
    return this.connectionStatus?.connected || false;
  }

  // ===========================================================================
  // Multi-account methods (mirrors gmailService's getAccounts/disconnect/updateSettings)
  // ===========================================================================

  /**
   * Get all connected Google Calendar accounts
   */
  async getAccounts(): Promise<GoogleCalendarAccountStatus[]> {
    try {
      const response = await this.edgeFetch('calendar-oauth', '/status');
      if (!response.ok) return [];
      const data = await response.json();
      return data.accounts || [];
    } catch (error) {
      console.error('[GoogleCalendar] Error getting accounts:', error);
      return [];
    }
  }

  /**
   * Disconnect a specific Calendar account
   */
  async disconnectAccount(accountId: string): Promise<void> {
    this.stopSync();
    const response = await this.edgeFetch('calendar-oauth', `/disconnect?account_id=${encodeURIComponent(accountId)}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to disconnect');
    }
    this.connectionStatus = null;
  }

  /**
   * Update settings for a specific Calendar account
   */
  async updateAccountSettings(accountId: string, settings: {
    defaultCalendarId?: string;
    syncEnabled?: boolean;
    syncDirection?: 'read' | 'write' | 'both';
    syncFrequencyMinutes?: number;
    calendarStartHour?: number;
  }): Promise<void> {
    const response = await this.edgeFetch('calendar-oauth', '/settings', {
      method: 'POST',
      body: JSON.stringify({ ...settings, accountId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update settings');
    }
    this.connectionStatus = null;
  }

  /**
   * Update Calendar settings
   */
  async updateSettings(settings: {
    defaultCalendarId?: string;
    syncEnabled?: boolean;
    syncDirection?: 'read' | 'write' | 'both';
    syncFrequencyMinutes?: number;
    calendarStartHour?: number;
  }): Promise<boolean> {
    const response = await this.edgeFetch('calendar-oauth', '/settings', {
      method: 'POST',
      body: JSON.stringify(settings),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update settings');
    }

    // Update local settings
    if (settings.syncDirection) {
      this.saveLocalSettings({ syncDirection: settings.syncDirection });
    }
    if (settings.calendarStartHour !== undefined) {
      this.saveLocalSettings({ calendarStartHour: settings.calendarStartHour });
    }

    // Clear cached status
    this.connectionStatus = null;
    return true;
  }

  /**
   * Disconnect from Google Calendar
   */
  async disconnect(): Promise<void> {
    this.stopSync();

    const response = await this.edgeFetch('calendar-oauth', '/disconnect', {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to disconnect');
    }

    this.connectionStatus = null;
  }

  /**
   * Generate random state for OAuth
   */
  private generateState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * List user's calendars
   */
  async listCalendars(): Promise<CalendarListEntry[]> {
    const response = await this.edgeFetch('calendar-api', '/calendars');

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch calendars');
    }

    const data = await response.json();
    return data.items || [];
  }

  /**
   * Get events for a specific date range
   */
  async getEvents(
    startDate: Date,
    endDate: Date,
    calendarId?: string
  ): Promise<GoogleCalendarEvent[]> {
    const cacheKey = this.eventsCacheKey(startDate, endDate, calendarId);
    const cached = this.eventsCache.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < EVENTS_CACHE_TTL_MS) {
      return cached.events;
    }

    const params = new URLSearchParams({
      timeMin: startDate.toISOString(),
      timeMax: endDate.toISOString(),
      singleEvents: 'true',
      orderBy: 'startTime',
    });

    if (calendarId) {
      params.append('calendarId', calendarId);
    }

    const response = await this.edgeFetch('calendar-api', `/events?${params}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch events');
    }

    const data = await response.json();
    const events: GoogleCalendarEvent[] = data.items || [];
    this.eventsCache.set(cacheKey, { events, fetchedAt: Date.now() });
    return events;
  }

  /**
   * Get events for a specific date (single day)
   */
  async getEventsForDate(date: Date, calendarId?: string): Promise<GoogleCalendarEvent[]> {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    return this.getEvents(startOfDay, endOfDay, calendarId);
  }

  /**
   * Get events for a month
   */
  async getEventsForMonth(year: number, month: number, calendarId?: string): Promise<GoogleCalendarEvent[]> {
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);

    return this.getEvents(startDate, endDate, calendarId);
  }

  /**
   * Create a new event
   */
  async createEvent(
    event: Omit<GoogleCalendarEvent, 'id' | 'status' | 'htmlLink'>,
    calendarId?: string
  ): Promise<GoogleCalendarEvent> {
    if (this.localSettings.syncDirection === 'read') {
      throw new Error('Calendar is in read-only mode');
    }

    const params = calendarId ? `?calendarId=${encodeURIComponent(calendarId)}` : '';
    const response = await this.edgeFetch('calendar-api', `/events${params}`, {
      method: 'POST',
      body: JSON.stringify(event),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create event');
    }

    this.invalidateEventsCache();
    return response.json();
  }

  /**
   * Update an existing event
   */
  async updateEvent(
    eventId: string,
    event: Partial<GoogleCalendarEvent>,
    calendarId?: string
  ): Promise<GoogleCalendarEvent> {
    if (this.localSettings.syncDirection === 'read') {
      throw new Error('Calendar is in read-only mode');
    }

    const params = calendarId ? `?calendarId=${encodeURIComponent(calendarId)}` : '';
    const response = await this.edgeFetch('calendar-api', `/events/${eventId}${params}`, {
      method: 'PUT',
      body: JSON.stringify(event),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update event');
    }

    this.invalidateEventsCache();
    return response.json();
  }

  /**
   * Delete an event
   */
  async deleteEvent(eventId: string, calendarId?: string): Promise<void> {
    if (this.localSettings.syncDirection === 'read') {
      throw new Error('Calendar is in read-only mode');
    }

    const params = calendarId ? `?calendarId=${encodeURIComponent(calendarId)}` : '';
    const response = await this.edgeFetch('calendar-api', `/events/${eventId}${params}`, {
      method: 'DELETE',
    });

    if (!response.ok && response.status !== 404) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete event');
    }

    this.invalidateEventsCache();
  }

  /**
   * Test connection to Google Calendar
   */
  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      const status = await this.getConnectionStatus();

      if (!status.connected) {
        return { success: false, message: 'Not connected to Google Calendar' };
      }

      if (status.isExpired) {
        return { success: false, message: 'Calendar connection expired. Please reconnect.' };
      }

      const calendars = await this.listCalendars();
      const primaryCalendar = calendars.find(c => c.primary);

      return {
        success: true,
        message: `Connected to ${primaryCalendar?.summary || 'Google Calendar'} (${calendars.length} calendars)`,
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Connection failed',
      };
    }
  }

  /**
   * Convert Google Calendar event to TimeBlock format (for Daily Notes)
   */
  eventToTimeBlock(event: GoogleCalendarEvent): {
    id: string;
    startTime: string;
    endTime: string;
    title: string;
    type: 'meeting' | 'focus' | 'break';
    googleEventId: string;
    isGoogleEvent: true;
  } | null {
    // Handle all-day events (no specific time)
    if (!event.start.dateTime || !event.end.dateTime) {
      return null;
    }

    const startDate = new Date(event.start.dateTime);
    const endDate = new Date(event.end.dateTime);

    return {
      id: `gcal-${event.id}`,
      startTime: startDate.toTimeString().substring(0, 5), // HH:MM format
      endTime: endDate.toTimeString().substring(0, 5),
      title: event.summary,
      type: 'meeting', // Default to meeting for Google Calendar events
      googleEventId: event.id,
      isGoogleEvent: true,
    };
  }

  /**
   * Start auto-sync interval
   */
  startSync(): void {
    if (this.syncInterval) {
      return;
    }

    const intervalMs = 15 * 60 * 1000; // Default 15 minutes
    this.syncInterval = setInterval(() => {
      this.performSync();
    }, intervalMs);

    // Perform initial sync
    this.performSync();
  }

  /**
   * Stop auto-sync
   */
  stopSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Perform sync operation
   */
  private async performSync(): Promise<void> {
    try {
      const status = await this.getConnectionStatus();
      if (!status.connected || status.isExpired) {
        return;
      }

      // Get today's events and notify listeners
      const today = new Date();
      const events = await this.getEventsForDate(today);

      // Notify all listeners for today's date
      const dateKey = today.toISOString().split('T')[0];
      const listeners = this.eventListeners.get(dateKey);
      if (listeners) {
        listeners.forEach(callback => callback(events));
      }

      // Mark sync complete on server
      await this.edgeFetch('calendar-api', '/sync-complete', { method: 'POST' });
    } catch (error) {
      console.error('Sync error:', error);
    }
  }

  /**
   * Subscribe to events for a specific date
   */
  subscribeToDate(date: Date, callback: (events: GoogleCalendarEvent[]) => void): () => void {
    const dateKey = date.toISOString().split('T')[0];

    if (!this.eventListeners.has(dateKey)) {
      this.eventListeners.set(dateKey, new Set());
    }

    this.eventListeners.get(dateKey)!.add(callback);

    // Return unsubscribe function
    return () => {
      const listeners = this.eventListeners.get(dateKey);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.eventListeners.delete(dateKey);
        }
      }
    };
  }

  /**
   * Get calendar start hour setting
   */
  getCalendarStartHour(): number {
    return this.localSettings.calendarStartHour;
  }

  /**
   * Get current settings (for compatibility)
   */
  getSettings(): { calendarStartHour: number; syncDirection: 'read' | 'write' | 'both' } {
    return {
      calendarStartHour: this.localSettings.calendarStartHour,
      syncDirection: this.localSettings.syncDirection,
    };
  }

  /**
   * Initialize service (call on app start)
   */
  async initialize(): Promise<void> {
    try {
      const status = await this.getConnectionStatus();
      if (status.connected && status.syncEnabled && !status.isExpired) {
        this.startSync();
      }
    } catch (error) {
      console.error('Failed to initialize Calendar service:', error);
    }
  }
}

// Export singleton instance
export const googleCalendarService = new GoogleCalendarService();
